define((require) => {
    "use strict";

    var Backbone                        = require("backbone"),
        Marionette                      = require("marionette"),
        _                               = require("underscore"),
        $                               = require("jquery"),
        SignInView                      = require("ui/user/SignInView"),
        RegistrationView                = require("ui/user/RegistrationView"),
        ResetPasswordView               = require("ui/user/ResetPasswordView"),
        ForgotPasswordView              = require("ui/user/ForgotPasswordView"),
        BrowserSupportView              = require("ui/user/BrowserSupportView"),
        SignInNavBarView                = require("ui/user/SignInNavBarView"),
        Login                           = require("security/Login"),
        LoggedIn                        = require("shared/security/LoggedIn"),
        LoggedInUserState               = require("shared/ui/user/LoggedInUserState"),
        LoggedInUserStateLoader         = require("ui/user/LoggedInUserStateLoader"),
        ExperienceURLProvider           = require("ui/experience/ExperienceURLProvider"),
        NotificationBar                 = require("editor/ui/notificationbar/NotificationBar");

    function getUsername() { // not the display name, actual username (email)
        var user = LoggedIn.user();
        return user && user.get("username");
    }

    /**
     * Controller for authentication and the sign in view.
     *
     * @author Bo Gotthardt
     * @class AuthenticationController
     */
    return Marionette.Controller.extend({

        /**
         * @private
         * Loads all data for the user described by the now logged in principal, using the loader options
         * set for this authentication controller.
         *
         * @param {Principal} principal The principal just logged in; never null.
         * @return {Promise} The promise that will resolve once all user relevant (as configured) has loaded; never falsey.
         */
        _load: function(principal) {
            return LoggedInUserStateLoader.load(principal, this.options.loader);
        },

        /**
         * Show the login form.
         *
         * @param   {Boolean} [adLogin] Whether to show the Active Directory version of the login form immediately.
         * @returns {Promise} Promise that resolves when the principal has logged in and all the global data for
         *                    the associated user has been loaded, with two parameters: the principal, and optionally
         *                    the AD login model.
         */
        showLoginForm: function(adLogin) {
            var deferred = $.Deferred(),
                username = getUsername();

            this.options.region.show(new SignInView({
                loginDeferred: deferred,
                username:      username,
                adLogin:       !!adLogin
            }));

            // If we have a username, it is because we have a read-only token and want to upgrade to a normal
            // token again, which is why we keep the nav bar region as is, e.g. to allow the user to click "back", etc...
            if (!username) {
                this.options.navBarRegion.show(new SignInNavBarView());
            }

            return deferred.promise();
        },

        /**
         * Show the forgot password form.
         */
        showForgotPasswordForm: function() {
            var username = getUsername();

            this.options.region.show(new ForgotPasswordView({username: username}));

            // If we have a username, it is because we have a read-only token and want to upgrade to a normal
            // token again, which is why we keep the nav bar region as is, e.g. to allow the user to click "back", etc...
            if (!username) {
                this.options.navBarRegion.show(new SignInNavBarView());
            }
        },

        /**
         * Show the reset password form for the supplied reset token (from a link clicked in an email).
         *
         * @param   {String}  resetToken The rest token (from the reset url).
         * @returns {Promise} Promise that resolves when the password has successfully been reset and the principal has
         *                    logged in and all the global data for the associated user has been loaded.
         */
        showResetPasswordForm: function(resetToken) {
            var deferred = $.Deferred();

            this.options.region.show(new ResetPasswordView({
                loginDeferred: deferred,
                resetToken:    resetToken
            }));

            this.options.navBarRegion.show(new SignInNavBarView());

            return deferred.promise();
        },

        /**
         * Show the browser support form.
         */
        showBrowserSupport: function() {
            this.options.region.show(new BrowserSupportView()); // TODO: why is this on the authentication controller?
        },

        /**
         * Attempts a login using the login form.
         *
         * @param {Boolean} [adLogin]  Whether to show the Active Directory version of the login form immediately.
         * @param {String}  [fragment] Optional fragment to navigate to once the user has successfully signed in. If
         *                             not specified, the current route will be reloaded.
         *
         * @returns {Promise} Promise that resolves when the principal is logged in and all global data for the
         *                    associated user has been loaded; never null.
         */
        loginWithLoginForm: function(adLogin, fragment) {
            return this.showLoginForm(adLogin)
                .then((principal, adLoginModel) => {
                    return this._load(principal).done(() => {
                        if (adLoginModel) {
                            // Navigate to the experience if defined
                            var experienceId = adLoginModel.get("experienceId");
                            if (Backbone.history.fragment.indexOf("adLogin") === 0) {
                                Backbone.history.navigate("", true);
                            } else if (experienceId && Backbone.history.fragment.indexOf("experience/") === -1) {
                                Backbone.history.navigate(ExperienceURLProvider.provide(experienceId, ExperienceURLProvider.ACTION.EDIT), true);

                            // Reload, which will trigger the logic that reads the token from the cookie.
                            } else {
                                Backbone.history.loadUrl(Backbone.history.fragment);
                            }
                        } else if (_.isString(fragment)) {
                            Backbone.history.navigate(fragment, {trigger: true, replace: true}); // TODO: Make the "replace" optional?
                        } else {
                            // Reload the current route
                            Backbone.history.loadUrl(Backbone.history.fragment);
                        }
                    });
                });
        },

        /**
         * Attempts to sign in with a potential persisted principal, i.e. the user has previously signed in, with
         * the "Keep me signed in" option checked. If this fails, the login form is used.
         *
         * @return {Promise} A promise for the principal having logged in and loaded all global data for the associated
         *                   user successfully.
         */
        loginWithPersistedUserOrLoginForm: function() {
            return Login.loginPersistedUser()
                .fail(() => {
                    this.loginWithLoginForm(false);
                })
                .then((principal) => {
                    return this._load(principal);
                });
        },

        /**
         * Attempts to log in a principal on behalf of an entirely new Creator user using a token supplied in the link,
         * defaulting to the persisted user. <p>
         *
         * If this fails, the login form is used.
         *
         * @param {String} token   The access token for the new Creator user; cannot be null.
         * @param {String} isShare Whether the request comes from a share action or just a new user action.
         *
         * @return {Promise} A promise for the principal having logged in and loaded all global data for the associated
         *                   new user successfully.
         */
        loginWithNewUserTokenOrLoginForm: function(token, isShare) {
            var scope = this;
            // Try and log in with user from token ...
            return Login.loginNewUser(token)
                .fail(() => {
                    // The token is only valid the first time used, so try and see if the principal has a persistent
                    // login in to use, otherwise go to the login form...
                    return Login.loginPersistedUser().then((principal) => {
                        return scope._load(principal).then(() => {
                            // Navigate to the dashboard once done...
                            Backbone.history.navigate("#", true);
                        });
                    }).fail(() => {
                        return scope.loginWithLoginForm(false, "#"); // go to dashboard...
                    });
                })
                .then((principal) => {
                    // Show the new user overlay...
                    $("body").append(new RegistrationView({model: principal.get("user"), isShare: isShare}).render().$el); // TODO: this is ugly, most be a better way to show an overlay in a generic fashion?!
                    // Resolve the promise once the user information is completely loaded...
                    return scope._load(principal);
                });
        },

        /**
         * Attempts a login after a successful password reset based on a reset token.
         *
         * @param   {String}  resetToken The rest token (from the reset url).
         *
         * @returns {Promise} Promise that resolves when the password has been successfully reset and the principal
         *                    is logged in and all global data for the associated user has been loaded; never null.
         */
        loginAfterPasswordReset: function(resetToken) {
            return this.showResetPasswordForm(resetToken)
                .then((principal) => {
                    return this._load(principal).done(() => {
                        // Navigate to the dashboard once reset successfully...
                        Backbone.history.navigate("#", true);
                        NotificationBar.open("Your password has successfully been reset.", {closeDelay: 7500});
                    });
                });
        },

        /**
         * Attempts to log in a principal on behalf of a user who tries to login from the Zmags website. <p>
         *
         * The login request contains the user token.
         *
         * If the login via token fails, the login form is used.
         *
         * @param {String} token The token to login with; cannot be null.
         * @return {Promise} A promise for the principal having logged in and loaded all global data for the associated
         *                   new user successfully, then navigated to the dashboard.
         */
        loginFromZmagsRequest: function(token) {
            return Login.loginNewUser(token)
                .fail(() => {
                    this.loginWithLoginForm(false);
                })
                .then((principal) => {
                    return this._load(principal).done(() => {
                        // Navigate to the dashboard once done...
                        Backbone.history.navigate("#", true);
                    });
                });
        },

        /**
         * This method attempts to log in a principal on behalf of a user and redirect to experience/:id/edit/:scene 
         * when user tries to edit experiece who is redirected from Content Control UI (Preview Page).
         * 
         * In case if the login via token fails, user will redirected to Content Control UI Dashboard.
         * 
         * @param  {String} token, User token which used to do  login.
         * @param  {String} redirectUrl
         * @param  {String} contentCtrlDashboard, dashboard url in case if login fail will redirect to this url.
         */
        loginWithToken: function(token, redirectUrl, contentCtrlDashboard) {
            return Login.loginNewUser(token)
                .fail(() => {
                    window.open(contentCtrlDashboard, '_self');
                })
                .then((principal) => {
                    return this._load(principal).done(() => {
                        // Navigate to the Edit experience once done...
                        window.open(redirectUrl, '_self');
                    });
                });
        },

        /**
         * Logs the current principal out and clears all global cached state for the associated user. <p>
         *
         * This will trigger a change event for <tt>LoggedInUserState.ready</tt> with a false value.
         */
        logout: function() {
            Login.logout();
            LoggedInUserState.clear();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/security/AuthenticationController.js